import * as React from 'react';

import type { ContentRefReusableContent, Space } from '@gitbook/api-client';
import { reusableContent as reusableContentSchema } from '@gitbook/doc-core';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveBranch, useHiveClient } from '@gitbook/hive-react';
import {
    createPalette,
    type PaletteCommandDef,
    type PaletteGroupDef,
    PalettePositionWidth,
} from '@gitbook/react-uikit';
import { type Block, BlockRange, isBlockRangeSelection } from '@gitbook/slate';
import { useEditor } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import type { IconDefinition } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { getSuspense } from '@gitbook/util-suspense-cache';
import { assertIsDefined, filterOutNullable } from '@gitbook/util-ts';

import { apiResource, readAPICache, useCurrentOrganization } from '../../../state';
import { capitalizeFirstLetter } from '../../../utils';
import { flattenSpacesInOrgAllContent } from '../../Organization';
import { SpaceIcon } from '../../Space';
import { ContentEditorContext } from '../contexts';
import {
    useCheckTurnableIntoReusableContent,
    useTurnIntoReusableContent,
} from './turnIntoReusableContent';

/**
 * Returns a palette group definition for turning a block into reusable content.
 */
export function useReusableContentPaletteGroup(input: {
    nodeKey: string;
    /** Do not show if any block is not supported. */
    strict?: boolean;
    method: 'editor-palette' | 'editor-prompt-on-copy';
    title?: string;
}): () => PaletteGroupDef | null {
    const editor = useEditor();
    const checkTurnableIntoReusableContent = useCheckTurnableIntoReusableContent();
    const turnIntoReusableContent = useTurnIntoReusableContent();

    return useEventCallback(() => {
        const { nodeKey, method, title } = input;
        const { selection } = editor.value;
        const block = editor.value.document.assertBlock(nodeKey);
        const blockRange = isBlockRangeSelection(selection)
            ? selection
            : BlockRange.createAtBlock(block);

        const result = checkTurnableIntoReusableContent({ editor, blockRange });

        if (input.strict && result) {
            return null;
        }

        if (result?.reason === 'no-support' || result?.reason === 'only-one-reusable-content') {
            return null;
        }

        return {
            key: 'reusable-content',
            commands: [
                {
                    key: 'reusable-content',
                    title: title ?? 'Turn into reusable content',
                    icon: Icon.ReusableContent,
                    trigger: () => turnIntoReusableContent({ blockRange, method }),
                    disabled: Boolean(result),
                    subtitle: result
                        ? `${result.unsupportedBlockTypes
                              .map((type) => capitalizeFirstLetter(type))
                              .join(', ')} not supported.`
                        : undefined,
                },
            ],
        };
    });
}

type InsertOptions = {
    onSelectReusableContent: (block: Block, ref: ContentRefReusableContent) => void;
};

/**
 * Returns a palette command definition for inserting a reusable content.
 */
export function useReusableContentPaletteCommandsFactory(
    options: InsertOptions
): null | (() => () => PaletteCommandDef[]) {
    const {
        blocks: { withReusableContent },
    } = React.useContext(ContentEditorContext);
    const hiveClient = useHiveClient();
    const hiveBranch = useHiveBranch();
    const organization = useCurrentOrganization();

    const create = React.useCallback(() => {
        return () => {
            assertIsDefined(hiveBranch);

            const content = readAPICache(
                apiResource(
                    'getOrganizationAllContent',
                    {
                        organizationId: organization.id,
                    },
                    { nullOnError: true }
                )
            );

            const otherSpaces = content
                ? flattenSpacesInOrgAllContent(content).filter(
                      (other) => other.id !== hiveBranch?.contentId
                  )
                : [];

            const sameSpaceReusableContent = createReusableContentCommands(
                hiveBranch.revision,
                options.onSelectReusableContent,
                Icon.ReusableContent
            );

            const otherSpacesReusableContents: PaletteCommandDef = {
                key: 'reusable-content-other-spaces',
                title: 'From other spaces',
                icon: Icon.ReusableContentExternal,
                keywords: ['reusable', 'synced', 'include'],
                descendant: () => {
                    return createPalette({
                        search: { placeholder: 'Insert reusable content…' },
                        position: {
                            width: PalettePositionWidth.LARGE,
                        },
                        groups: otherSpaces
                            .map((space) => {
                                return {
                                    key: space.id,
                                    title: space.title,
                                    icon: <SpaceIcon space={space} />,
                                    keywords: ['reusable', 'synced', 'include'],
                                    commands: () => {
                                        const revision = getSuspense(
                                            hiveClient.store.getBranchRevision(
                                                space.id,
                                                hiveCore.CONTENT_DEFAULT_BRANCH
                                            )
                                        );

                                        if (
                                            !revision ||
                                            Object.keys(revision.reusableContents).length === 0
                                        ) {
                                            return [];
                                        }

                                        return createReusableContentCommands(
                                            revision,
                                            options.onSelectReusableContent,
                                            Icon.ReusableContentExternal,
                                            space
                                        );
                                    },
                                };
                            })
                            .filter(filterOutNullable),
                    });
                },
            };

            return [
                ...sameSpaceReusableContent,
                ...(otherSpacesReusableContents ? [otherSpacesReusableContents] : []),
            ];
        };
    }, [organization.id, hiveBranch, options.onSelectReusableContent]);

    return withReusableContent ? create : null;
}

function createReusableContentCommands(
    revision: hiveCore.Revision,
    onSelectReusableContent: (block: Block, ref: ContentRefReusableContent) => void,
    icon: IconDefinition,
    space?: Space
): PaletteCommandDef[] {
    return Object.values(revision.reusableContents).map((reusableContent) => {
        return {
            key: reusableContent.id,
            title: reusableContent.title,
            icon,
            keywords: ['reusable', 'synced', 'include', ...(space ? [space.title] : [])],
            trigger: () => {
                const ref: ContentRefReusableContent = {
                    kind: 'reusable-content',
                    reusableContent: reusableContent.id,
                    ...(space ? { space: space.id } : {}),
                };
                const block = reusableContentSchema.createReusableContentBlock(ref);
                onSelectReusableContent(block, ref);
            },
        };
    });
}
